/**
 * Shared helpers for report forms.
 */
import {
  REMOTE_REPORT_SESSION_TYPE_CONFIG,
  REMOTE_REPORT_TAB_COLORS,
  REMOTE_REPORT_TAB_LABELS,
  REMOTE_REPORT_TAB_SESSION_TYPES,
  REPORT_REMOTE_SCHEMA,
  REPORT_REMOTE_TAB_TO_SCHEMA,
  type RemoteSchemaId,
} from '@/components/reports/report-configs';
import type {
  ReportPermissions,
  ReportTab as Tab,
  ReportScoringEntry,
} from '@/types/remote/report.types';
import { getErrorMessage } from '@/utils/errors-utils';

/** True when the schema is the tutoring report. */
export function isTutoringReportSchema(schemaId: string): boolean {
  return schemaId === REPORT_REMOTE_SCHEMA.tutoring;
}

/** True when the schema is an information/welcome synthesis. */
export function isSynthesisReportSchema(schemaId: string): boolean {
  return (
    schemaId === REPORT_REMOTE_SCHEMA.synthesisInformationMeeting ||
    schemaId === REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting
  );
}

/** Read-only when the actor lacks write, or a tutoring report is already `valide`. */
export function isReportReadOnly(
  permissions: ReportPermissions,
  schemaId: string,
  status: string,
): boolean {
  return (
    permissions.compteRendu !== 'write' || (isTutoringReportSchema(schemaId) && status === 'valide')
  );
}

/** Shallow-clone draft fields so the editor can mutate freely. */
export function initReportDraftFields(fields: Record<string, unknown>): Record<string, unknown> {
  return { ...fields };
}

/** Merge a field patch onto the draft. */
export function applyReportFieldPatch(
  prev: Record<string, unknown>,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  return { ...prev, ...patch };
}

/** Central error copy from {@link getErrorMessage}. */
export function getReportPatchApiError(error: unknown): string {
  return getErrorMessage(error);
}

/** Read-only banner: validated vs missing write permission. */
export function reportReadOnlyMessage(status: string): string {
  if (status === 'valide') return getErrorMessage('reportValidatedReadOnly');
  return getErrorMessage('reportNoWritePermission');
}

/** True when the report has at least one scoring entry. */
export function hasScoringEntries(entries: ReportScoringEntry[] | undefined): boolean {
  return (entries?.length ?? 0) > 0;
}

/** Emit a single-key patch through `onChange`. */
export function setReportField(
  onChange: (patch: Record<string, unknown>) => void,
  key: string,
  value: unknown,
): void {
  onChange({ [key]: value });
}

/** Schema for a tab; `all` falls back to tutoring. */
export function resolveSchemaId(sessionType: Tab): RemoteSchemaId {
  if (sessionType === 'all') return REPORT_REMOTE_SCHEMA.tutoring;
  return REPORT_REMOTE_TAB_TO_SCHEMA[sessionType];
}

/** Create-form type metadata (label/color) for the current tab. */
export function resolveCreateTypeMeta(sessionType: Tab) {
  const direct = REMOTE_REPORT_SESSION_TYPE_CONFIG[sessionType];
  if (direct) return direct;
  const firstType = REMOTE_REPORT_TAB_SESSION_TYPES[sessionType]?.[0];
  const fromType = firstType ? REMOTE_REPORT_SESSION_TYPE_CONFIG[firstType] : undefined;
  if (!fromType) return null;
  return {
    ...fromType,
    label: REMOTE_REPORT_TAB_LABELS[sessionType] ?? fromType.label,
    badgeColor: REMOTE_REPORT_TAB_COLORS[sessionType] ?? fromType.badgeColor,
  };
}
